import React, { useEffect, useMemo, useReducer, useState, useRef, useCallback } from "react";
import { Spin, Modal, Button, Select, Typography, Space, message } from "antd";
import { CloseOutlined, WarningOutlined } from "@ant-design/icons";
import { throttle } from "throttle-debounce";
import { ClientAutoCompleteDropdown } from "./client-auto-complete-dropdown";
import { ClientForm } from "./client-form";
import { ClientFilterData, ClientValue, NewClientValue } from "../../data-types";
import { InfiniteScrollSelect, InfiniteScrollSelectProps } from "../infinite-scroll-select";

import styles from "./client-auto-complete.css";
import _ from "lodash";

const {
  ["dropdown-no-options"]: dropdownNoOptionsClassName,
  ["warning-container"]: warningContainerClassName,
  ["warning-message"]: warningMessageClassName,
  ["warning-action"]: warningActionClassName,
} = styles as any;

declare type ClientAutoCompleteProps = {
  defaultValue?: ClientValue | ClientValue[] | null;
  value?: ClientValue | ClientValue[] | null;
  disabled?: boolean;
  onChangeClient?: (client: ClientValue) => void;
  onChangeMultipleClients?: (clients: ClientValue[]) => void;
  onClientSave: (client: NewClientValue) => void;
  clientFilterData: ClientFilterData;
  bordered?: boolean;
  classname?: string;
  debounceTimeout?: number;
  mode?: "multiple" | "tags";
  searchValue?: string;
  canCreateClient?: boolean;
  checkForDuplicates?: boolean;
  pmsName?: string;
} & Pick<InfiniteScrollSelectProps<any>, "onClear">;

declare type ClientOption = {
  client: ClientValue;
  value: string;
  label: JSX.Element;
  selectedString: string;
};

interface DuplicateState {
  isFixModalVisible: boolean;
  selectedDuplicateId: string | number | null;
  newClientValues: NewClientValue | null;
  isNewClientDuplicateCheck: boolean;
  asyncDuplicates: ClientValue[];
}

type DuplicateAction =
  | { type: "SHOW_MODAL"; payload?: { isNewClient: boolean } }
  | { type: "HIDE_MODAL" }
  | { type: "SET_SELECTED_DUPLICATE"; payload: { id: string | number | null } }
  | { type: "SET_NEW_CLIENT_VALUES"; payload: { values: NewClientValue | null } }
  | { type: "SET_ASYNC_DUPLICATES"; payload: { duplicates: ClientValue[] } }
  | { type: "RESET_ALL" };

interface DisplayState {
  searchOpen: boolean;
  addNewOpen: boolean;
}

type DisplayAction =
  | { type: "open-search" }
  | { type: "close-search" }
  | { type: "open-add-new" }
  | { type: "close-add-new" };

const { Text } = Typography;
const { Option } = Select;

const FirstSearch = (
  <>
    <h2>Search for a Client</h2>
    <h4>Start typing to see Clients that match your search term.</h4>
  </>
);

// Custom hook for duplicate checking
function useDuplicateCheck({
  clientFilterData,
  checkForDuplicates,
  onClientSave,
  pmsName,
  displayDispatch,
}: {
  clientFilterData: ClientFilterData;
  checkForDuplicates?: boolean;
  onClientSave: (client: NewClientValue) => void;
  pmsName?: string;
  displayDispatch?: React.Dispatch<DisplayAction>;
}) {
  // Duplicate state reducer
  const duplicateReducer = (state: DuplicateState, action: DuplicateAction): DuplicateState => {
    switch (action.type) {
      case "SHOW_MODAL":
        return {
          ...state,
          isFixModalVisible: true,
          isNewClientDuplicateCheck: action.payload?.isNewClient ?? state.isNewClientDuplicateCheck,
        };
      case "HIDE_MODAL":
        return {
          ...state,
          isFixModalVisible: false,
        };
      case "SET_SELECTED_DUPLICATE":
        return {
          ...state,
          selectedDuplicateId: action.payload.id,
        };
      case "SET_NEW_CLIENT_VALUES":
        return {
          ...state,
          newClientValues: action.payload.values,
        };
      case "SET_ASYNC_DUPLICATES":
        return {
          ...state,
          asyncDuplicates: action.payload.duplicates,
        };
      case "RESET_ALL":
        return {
          isFixModalVisible: false,
          selectedDuplicateId: null,
          newClientValues: null,
          isNewClientDuplicateCheck: false,
          asyncDuplicates: [],
        };
      default:
        return state;
    }
  };

  const [duplicateState, duplicateDispatch] = useReducer(duplicateReducer, {
    isFixModalVisible: false,
    selectedDuplicateId: null,
    newClientValues: null,
    isNewClientDuplicateCheck: false,
    asyncDuplicates: [],
  });

  // Memoized function to check for duplicates when saving a new client
  const checkDuplicatesAndSave = useCallback(
    async (values: NewClientValue) => {
      // If duplicate checking is disabled, just save the client
      if (!checkForDuplicates || !clientFilterData.checkForDuplicates) {
        onClientSave(values);
        return;
      }

      try {
        // Reset state and store new client values
        duplicateDispatch({ type: "SET_NEW_CLIENT_VALUES", payload: { values } });
        duplicateDispatch({ type: "SET_ASYNC_DUPLICATES", payload: { duplicates: [] } });

        const duplicateResult = await clientFilterData.checkForDuplicates(values.name);

        if (duplicateResult.hasDuplicates && duplicateResult.duplicates.length > 0) {
          if (displayDispatch) {
            displayDispatch({ type: "close-search" });
          }

          duplicateDispatch({
            type: "SET_ASYNC_DUPLICATES",
            payload: { duplicates: duplicateResult.duplicates },
          });

          duplicateDispatch({
            type: "SHOW_MODAL",
            payload: { isNewClient: true },
          });

          // If only one duplicate, preselect it
          if (duplicateResult.duplicates.length === 1) {
            duplicateDispatch({
              type: "SET_SELECTED_DUPLICATE",
              payload: { id: duplicateResult.duplicates[0].id },
            });
          }
        } else {
          // No duplicates, save the client
          onClientSave(values);
          duplicateDispatch({ type: "RESET_ALL" });
        }
      } catch (error) {
        // In case of error, just save the client
        onClientSave(values);
      }
    },
    [checkForDuplicates, clientFilterData, onClientSave, displayDispatch]
  );

  // Handle proceeding with new client creation despite duplicates
  const handleProceedWithNewClient = useCallback(() => {
    if (duplicateState.newClientValues && onClientSave) {
      onClientSave(duplicateState.newClientValues);
      duplicateDispatch({ type: "RESET_ALL" });
    }
  }, [duplicateState.newClientValues, onClientSave]);

  // Get active duplicates based on the current mode
  const getActiveDuplicates = useCallback(() => {
    return duplicateState.isNewClientDuplicateCheck
      ? duplicateState.asyncDuplicates
      : clientFilterData.duplicateCheckResult.duplicates;
  }, [
    duplicateState.isNewClientDuplicateCheck,
    duplicateState.asyncDuplicates,
    clientFilterData.duplicateCheckResult.duplicates,
  ]);

  // Check if we have duplicates to show
  const hasDuplicates = useMemo(() => {
    return duplicateState.isNewClientDuplicateCheck
      ? duplicateState.asyncDuplicates.length > 0
      : clientFilterData.duplicateCheckResult.hasDuplicates;
  }, [
    duplicateState.isNewClientDuplicateCheck,
    duplicateState.asyncDuplicates.length,
    clientFilterData.duplicateCheckResult.hasDuplicates,
  ]);

  return {
    duplicateState,
    duplicateDispatch,
    checkDuplicatesAndSave,
    handleProceedWithNewClient,
    getActiveDuplicates,
    hasDuplicates,
    pmsName: pmsName || "PMS",
  };
}

const DuplicateResolutionModal: React.FC<{
  isVisible: boolean;
  isNewClientDuplicateCheck: boolean;
  selectedDuplicateId: string | number | null;
  duplicates: ClientValue[];
  pmsName: string;
  onCancel: () => void;
  onConfirmSelection: () => void;
  onCreateAnyway?: () => void;
  onSelectDuplicate: (id: string | number) => void;
}> = ({
  isVisible,
  isNewClientDuplicateCheck,
  selectedDuplicateId,
  duplicates,
  pmsName,
  onCancel,
  onConfirmSelection,
  onCreateAnyway,
  onSelectDuplicate,
}) => {
  return (
    <Modal
      title={isNewClientDuplicateCheck ? "Possible Duplicate Client" : "Use Existing Client"}
      open={isVisible}
      onCancel={onCancel}
      maskClosable={false}
      keyboard={false}
      destroyOnClose={true}
      closable={false}
      onOk={onConfirmSelection}
      okButtonProps={{ disabled: !selectedDuplicateId }}
      footer={
        isNewClientDuplicateCheck
          ? [
              <Button key="create" type="default" onClick={onCreateAnyway}>
                Create New Client Anyway
              </Button>,
              <Button
                key="use"
                type="primary"
                onClick={onConfirmSelection}
                disabled={!selectedDuplicateId}
              >
                Use Selected Client
              </Button>,
            ]
          : [
              <Button key="cancel" onClick={onCancel}>
                Cancel
              </Button>,
              <Button
                key="use"
                type="primary"
                onClick={onConfirmSelection}
                disabled={!selectedDuplicateId}
              >
                Use Selected Client
              </Button>,
            ]
      }
    >
      <Space direction="vertical" style={{ width: "100%" }}>
        <Text>
          {isNewClientDuplicateCheck
            ? duplicates.length === 1
              ? `A similar client "${duplicates[0].name}" already exists in ${pmsName}. Do you want to use this client instead of creating a new one?`
              : `Multiple similar clients were found in ${pmsName}. Please select the client you want to use or create a new client anyway:`
            : duplicates.length === 1
            ? `A matching client already exists in ${pmsName}. Do you want to use this client instead?`
            : `Multiple matching clients were found in ${pmsName}. Please select the client you want to use:`}
        </Text>

        {duplicates.length > 1 ? (
          <Select
            style={{ width: "100%" }}
            placeholder="Select a client"
            value={selectedDuplicateId}
            onChange={onSelectDuplicate}
          >
            {duplicates.map((client: ClientValue) => (
              <Option key={client.id} value={client.id}>
                {client.externalReference
                  ? `${client.name} - ${client.externalReference}`
                  : client.name}
              </Option>
            ))}
          </Select>
        ) : duplicates.length === 1 ? (
          <Text strong>
            {duplicates[0].externalReference
              ? `${duplicates[0].name} - ${duplicates[0].externalReference}`
              : duplicates[0].name}
          </Text>
        ) : null}
      </Space>
    </Modal>
  );
};

// display state reduction
function displayReducer(state: DisplayState, action: DisplayAction): DisplayState {
  switch (action.type) {
    case "open-search":
      return { searchOpen: true, addNewOpen: false };
    case "close-search":
      return { searchOpen: false, addNewOpen: false };
    case "open-add-new":
      return { searchOpen: false, addNewOpen: true };
    case "close-add-new":
      return { searchOpen: true, addNewOpen: false };
    default:
      return state;
  }
}

// get client value for display
function clientValue({ externalReference, name }: ClientValue) {
  return externalReference ? `${name} - ${externalReference}` : name;
}

// render client options
function renderClientOptions(clients: ClientValue[], searchTerm: string): ClientOption[] {
  const uniqueClients = _.uniqBy(clients, (client) => client.id);
  const filteredClients = uniqueClients.filter((client) => client !== undefined);
  return filteredClients.map((client) => {
    const { id } = client;

    const displayValue = clientValue(client);

    return {
      key: id,
      client,
      value: displayValue,
      selectedString: displayValue,
      label: getHighlightedMatch(searchTerm, displayValue),
    };
  });
}

const getHighlightedMatch = (search: string, text: string) => {
  if (!text.trim() || !search.trim()) {
    return <span>{text}</span>;
  }

  const regex = new RegExp(`(${_.escapeRegExp(search.trim())})`, "gi");
  const parts = text.split(regex);

  return (
    <span>
      {parts
        .filter((part) => part)
        .map((part, i) =>
          regex.test(part) ? (
            <span style={{ color: "red" }} key={i}>
              {part}
            </span>
          ) : (
            <span key={i}>{part}</span>
          )
        )}
    </span>
  );
};

export const ClientAutoComplete: React.FC<ClientAutoCompleteProps> = ({
  defaultValue,
  value,
  disabled = false,
  onChangeClient,
  onChangeMultipleClients,
  onClientSave,
  onClear,
  clientFilterData,
  classname,
  bordered = false,
  debounceTimeout = 800,
  mode,
  searchValue,
  canCreateClient = true,
  checkForDuplicates = true,
  pmsName,
}) => {
  const { search, fetchMore, total, clients, loading } = clientFilterData;

  const [displayState, displayDispatch] = useReducer(displayReducer, {
    searchOpen: false,
    addNewOpen: false,
  });

  const clientFilterDataRef = useRef(clientFilterData);
  useEffect(() => {
    clientFilterDataRef.current = clientFilterData;
  }, [clientFilterData]);

  const {
    duplicateState,
    duplicateDispatch,
    checkDuplicatesAndSave,
    handleProceedWithNewClient,
    getActiveDuplicates,
    hasDuplicates,
  } = useDuplicateCheck({
    clientFilterData,
    checkForDuplicates,
    onClientSave,
    pmsName,
    displayDispatch,
  });

  const [currentSearch, setCurrentSearch] = useState("");
  const stableModeRef = useRef(mode);
  const currentClientIdRef = useRef<string | number | undefined>();

  useEffect(() => {
    stableModeRef.current = mode;

    if (value && !Array.isArray(value)) {
      currentClientIdRef.current = value.id;
    }
  }, [mode, value]);

  const clientOptions = useMemo(
    () => renderClientOptions(clients || [], currentSearch),
    [clients, currentSearch]
  );

  const fetchRef = useRef(0);

  const onSearch = useMemo(() => {
    const loadOptions = (value: string) => {
      if (value.length > 1) {
        fetchRef.current += 1;
        const fetchId = fetchRef.current;
        setCurrentSearch(value);
        search(value);
        if (fetchId !== fetchRef.current) {
          return;
        }
      }
    };

    return throttle(debounceTimeout, loadOptions);
  }, [debounceTimeout, search]);

  useEffect(() => {
    if (
      Array.isArray(value) ||
      !checkForDuplicates ||
      displayState.searchOpen ||
      displayState.addNewOpen ||
      !clientFilterDataRef.current.checkForDuplicates ||
      value?.externalId ||
      value?.externalReference
    ) {
      return;
    }

    const currentValue = value ? clientValue(value) : "";

    if (currentValue && stableModeRef.current !== "multiple") {
      const checkDuplicates = async () => {
        if (!clientFilterDataRef.current.checkForDuplicates) return;

        await clientFilterDataRef.current.checkForDuplicates(
          currentValue,
          currentClientIdRef.current
        );
      };

      // Small delay to ensure UI is responsive
      const timeoutId = setTimeout(() => {
        checkDuplicates();
      }, 10);

      return () => clearTimeout(timeoutId);
    }
  }, [value, checkForDuplicates, displayState]);

  const handleFixDuplicate = useCallback(() => {
    displayDispatch({ type: "close-search" });
    duplicateDispatch({ type: "SHOW_MODAL" });

    const duplicates = getActiveDuplicates();

    // If only one duplicate, preselect it
    if (duplicates.length === 1) {
      duplicateDispatch({
        type: "SET_SELECTED_DUPLICATE",
        payload: { id: duplicates[0].id },
      });
    }
  }, [duplicateDispatch, getActiveDuplicates]);

  const handleConfirmSelection = useCallback(() => {
    const { selectedDuplicateId, isNewClientDuplicateCheck } = duplicateState;
    const duplicates = getActiveDuplicates();

    if (selectedDuplicateId && duplicates.length > 0) {
      const selectedClient = duplicates.find((client) => client.id === selectedDuplicateId);
      if (selectedClient) {
        if (isNewClientDuplicateCheck) {
          // When creating a new client, just select the existing one
          if (onChangeClient) {
            onChangeClient(selectedClient);
          }
        } else if (onChangeClient) {
          onChangeClient(selectedClient);
        }

        // Reset the duplicate check in the hook for both flows
        clientFilterData.resetDuplicateCheck?.();
        duplicateDispatch({ type: "RESET_ALL" });
      }
    }
  }, [duplicateState, getActiveDuplicates, clientFilterData, duplicateDispatch, onChangeClient]);

  const handleCancelSelection = useCallback(() => {
    duplicateDispatch({ type: "RESET_ALL" });

    if (!duplicateState.isNewClientDuplicateCheck) {
      clientFilterData.resetDuplicateCheck?.();
    }
  }, [duplicateDispatch, duplicateState.isNewClientDuplicateCheck, clientFilterData]);

  // Load more handler for infinite scroll
  const doOnLoadMore = useMemo(() => {
    return (clients?.length || 0) >= total
      ? undefined
      : () => {
          fetchMore();
        };
  }, [clients, total, fetchMore]);

  const firstSearch = clients === undefined;

  const NoOptions = useMemo(
    () => (
      <>
        <h2>No clients found</h2>
        {canCreateClient ? (
          <h4>Alter search terms or click &quot;Add New&quot; to create a new client.</h4>
        ) : (
          <h4>Try different search terms.</h4>
        )}
      </>
    ),
    [canCreateClient]
  );

  const NoOptionsOrFirstSearch = useMemo(
    () => <div className={dropdownNoOptionsClassName}>{firstSearch ? FirstSearch : NoOptions}</div>,
    [firstSearch, NoOptions]
  );

  return (
    <div style={{ position: "relative" }}>
      <InfiniteScrollSelect
        mode={mode}
        className={classname}
        optionLabelProp="selectedString"
        bordered={bordered}
        allowClear
        autoClearSearchValue={true}
        loading={loading && !duplicateState.asyncDuplicates.length}
        filterOption={false}
        clearIcon={<CloseOutlined />}
        onClear={() => {
          onClear?.();
          clientFilterData.resetDuplicateCheck?.();
        }}
        defaultValue={
          Array.isArray(defaultValue)
            ? defaultValue.map((v: any) => clientValue(v))
            : defaultValue
            ? clientValue(defaultValue)
            : undefined
        }
        value={
          Array.isArray(value)
            ? value.map((v: any) => clientValue(v))
            : value
            ? clientValue(value)
            : undefined
        }
        disabled={disabled}
        showSearch={true}
        notFoundContent={
          loading && !duplicateState.asyncDuplicates.length ? <Spin /> : NoOptionsOrFirstSearch
        }
        dropdownMatchSelectWidth
        options={clientOptions}
        onLoadMore={doOnLoadMore}
        onChange={(_, option) => {
          if (mode === "multiple") {
            return (
              option &&
              onChangeMultipleClients &&
              onChangeMultipleClients(
                Array.isArray(option) ? option.map((o) => o.client) : option.client
              )
            );
          } else {
            if (option && onChangeClient) {
              clientFilterData.resetDuplicateCheck?.();
              return onChangeClient(
                Array.isArray(option) ? option.map((o) => o.client) : option.client
              );
            }
            return false;
          }
        }}
        onSearch={onSearch}
        searchValue={searchValue}
        open={displayState.searchOpen || displayState.addNewOpen}
        onDropdownVisibleChange={(visible) =>
          displayDispatch({ type: visible ? "open-search" : "close-search" })
        }
        placeholder="Enter any part of client's name."
        dropdownRender={(menus) => {
          return displayState.addNewOpen ? (
            <ClientForm
              onSave={(values: NewClientValue) => {
                displayDispatch({ type: "close-add-new" });
                checkDuplicatesAndSave(values);
              }}
              onCancel={() => displayDispatch({ type: "close-add-new" })}
            />
          ) : (
            <ClientAutoCompleteDropdown
              onClickAddNew={() => {
                displayDispatch({ type: "open-add-new" });
              }}
              canCreateClient={canCreateClient}
            >
              {menus}
            </ClientAutoCompleteDropdown>
          );
        }}
      />

      {hasDuplicates && (
        <div className={warningContainerClassName}>
          <WarningOutlined />
          <span className={warningMessageClassName}>
            {(() => {
              const duplicates = getActiveDuplicates();
              return duplicates.length === 1
                ? `Similar ${pmsName ? pmsName : "PMS"} client found: ${
                    duplicates[0].externalReference
                      ? `${duplicates[0].name} - ${duplicates[0].externalReference}`
                      : duplicates[0].name
                  }`
                : `Multiple similar ${pmsName ? pmsName : "PMS"} clients found`;
            })()}
          </span>
          <Button type="primary" className={warningActionClassName} onClick={handleFixDuplicate}>
            Fix
          </Button>
        </div>
      )}

      <DuplicateResolutionModal
        isVisible={duplicateState.isFixModalVisible}
        isNewClientDuplicateCheck={duplicateState.isNewClientDuplicateCheck}
        selectedDuplicateId={duplicateState.selectedDuplicateId}
        duplicates={getActiveDuplicates()}
        pmsName={pmsName || "PMS"}
        onCancel={handleCancelSelection}
        onConfirmSelection={handleConfirmSelection}
        onCreateAnyway={handleProceedWithNewClient}
        onSelectDuplicate={(id) =>
          duplicateDispatch({ type: "SET_SELECTED_DUPLICATE", payload: { id } })
        }
      />
    </div>
  );
};
